import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { turboSummary, type TurboIngredient, type TurboPlan, type TurboSuggestion } from '@almox/shared';
import { api } from '../api/client.js';
import { useAppData } from '../AppData.js';
import { EmptyState, useToast } from '../components/ui.js';

/** Exemplos clicaveis: a tela em branco e o pior comeco para quem chega. */
const EXEMPLOS = [
  'Bolo de cenoura com cobertura para 5 pessoas',
  'Jantar simples para 4 pessoas hoje',
  'Lasanha para o almoco de domingo, 6 pessoas',
  'Marmitas da semana para 2 pessoas',
];

const SELO: Record<TurboIngredient['status'], { icone: string; classe: string; texto: string }> = {
  have: { icone: '✅', classe: 'badge badge-ok', texto: 'tem em casa' },
  missing: { icone: '🛒', classe: 'badge badge-danger', texto: 'falta' },
  unsure: { icone: '❔', classe: 'badge badge-warn', texto: 'conferir' },
};

/**
 * Modo turbo: o usuario diz o que vai fazer e a IA confere contra o estoque,
 * dizendo o que tem, o que falta e o que vale comprar. Nada entra na lista de
 * compras sozinho — o usuario aceita item a item ou tudo de uma vez.
 */
export function TurboPage() {
  const { settings, refresh } = useAppData();
  const toast = useToast();
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState(false);
  const [plan, setPlan] = useState<TurboPlan | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  /** Sugestoes ja aceitas nesta resposta, para nao repetir a mesma linha. */
  const [aceitas, setAceitas] = useState<string[]>([]);
  const [salvando, setSalvando] = useState(false);

  const disponivel = settings.photoAiAvailable && settings.photoAiEnabled;

  async function perguntar(evento: FormEvent) {
    evento.preventDefault();
    const pedido = prompt.trim();
    if (pedido.length < 3) return;

    setBusy(true);
    setPlan(null);
    setAviso(null);
    setAceitas([]);
    try {
      const resposta = await api.turboAsk(pedido);
      setPlan(resposta.plan);
      setAviso(resposta.message);
      if (!resposta.plan && resposta.message) toast(resposta.message, 'error');
    } catch (error) {
      toast((error as Error).message, 'error');
    } finally {
      setBusy(false);
    }
  }

  async function aceitar(sugestoes: TurboSuggestion[]) {
    if (sugestoes.length === 0) return;
    setSalvando(true);
    try {
      await api.addShoppingBulk(
        sugestoes.map((sugestao) => ({
          label: `${sugestao.label}${sugestao.unit !== 'un' ? ` (${sugestao.unit})` : ''}`,
          suggestedQuantity: sugestao.quantity,
          origin: 'manual' as const,
        })),
      );
      setAceitas((atual) => [...atual, ...sugestoes.map((sugestao) => sugestao.label)]);
      toast(
        sugestoes.length === 1
          ? `${sugestoes[0]?.label} foi para a lista de compras.`
          : `${sugestoes.length} itens foram para a lista de compras.`,
        'ok',
      );
      await refresh();
    } catch (error) {
      toast((error as Error).message, 'error');
    } finally {
      setSalvando(false);
    }
  }

  const pendentes = (plan?.suggestions ?? []).filter(
    (sugestao) => !aceitas.includes(sugestao.label),
  );

  return (
    <main className="page">
      <div className="page-head">
        <h1>⚡ Turbo</h1>
      </div>

      {!disponivel ? (
        <EmptyState
          icon="🔌"
          title="Modo turbo indisponivel"
          hint="Ele usa a mesma IA da leitura por foto. Cadastre a chave da OpenAI na administracao (ou ligue a IA em Configuracoes) para usar."
        />
      ) : (
        <>
          <section className="card">
            <h2>O que voce vai fazer?</h2>
            <p className="small muted" style={{ marginTop: 0 }}>
              Descreva o prato, a refeicao ou a tarefa. O Almox le o seu estoque e diz o que voce ja
              tem, o que falta e o que vale colocar na lista de compras.
            </p>
            <form onSubmit={perguntar}>
              <div className="field">
                <label htmlFor="turbo-prompt">Seu pedido</label>
                <textarea
                  id="turbo-prompt"
                  rows={3}
                  value={prompt}
                  maxLength={500}
                  onChange={(evento) => setPrompt(evento.target.value)}
                  placeholder="Ex.: bolo de cenoura com cobertura para 5 pessoas"
                />
              </div>
              <button
                type="submit"
                className="btn btn-primary btn-block"
                disabled={busy || prompt.trim().length < 3}
              >
                {busy ? 'Conferindo o estoque…' : '⚡ Conferir estoque'}
              </button>
            </form>

            <div className="row" style={{ marginTop: 12 }}>
              {EXEMPLOS.map((exemplo) => (
                <button
                  key={exemplo}
                  type="button"
                  className="btn btn-sm btn-ghost"
                  onClick={() => setPrompt(exemplo)}
                >
                  {exemplo}
                </button>
              ))}
            </div>
          </section>

          {aviso && !plan ? (
            <section className="card">
              <p className="small" style={{ margin: 0 }}>
                {aviso}
              </p>
            </section>
          ) : null}

          {plan ? (
            <>
              <section className="card">
                <h2>{turboSummary(plan)}</h2>
                {plan.understanding ? (
                  <p className="small muted" style={{ marginTop: 0 }}>
                    Entendi: {plan.understanding}
                  </p>
                ) : null}

                <ul className="list">
                  {plan.ingredients.map((ingrediente) => (
                    <li key={ingrediente.name} className="item">
                      <div className="photo">{SELO[ingrediente.status].icone}</div>
                      <div className="info">
                        <div className="title">{ingrediente.name}</div>
                        <div className="sub">
                          <span className={SELO[ingrediente.status].classe}>
                            {SELO[ingrediente.status].texto}
                          </span>
                          {ingrediente.needed ? <span>precisa de {ingrediente.needed}</span> : null}
                          {ingrediente.inStock ? <span>· {ingrediente.inStock}</span> : null}
                        </div>
                        {ingrediente.note ? (
                          <div className="small muted">{ingrediente.note}</div>
                        ) : null}
                      </div>
                    </li>
                  ))}
                </ul>

                {plan.notes ? <p className="hint-ia">💡 {plan.notes}</p> : null}
                <p className="small muted">
                  A IA compara nomes, nao pesa a despensa — confira antes de sair comprando.
                </p>
              </section>

              <section className="card">
                <h2>Para a lista de compras</h2>
                {plan.suggestions.length === 0 ? (
                  <p className="small muted" style={{ margin: 0 }}>
                    Nada a comprar: o que a receita pede ja esta em casa.
                  </p>
                ) : (
                  <>
                    {pendentes.length > 1 ? (
                      <button
                        type="button"
                        className="btn btn-primary btn-block"
                        disabled={salvando}
                        style={{ marginBottom: 12 }}
                        onClick={() => void aceitar(pendentes)}
                      >
                        ➕ Adicionar os {pendentes.length} itens de uma vez
                      </button>
                    ) : null}

                    <ul className="list">
                      {plan.suggestions.map((sugestao) => {
                        const aceita = aceitas.includes(sugestao.label);
                        return (
                          <li key={sugestao.label} className="item">
                            <div className="photo">{aceita ? '✅' : '🛒'}</div>
                            <div className="info">
                              <div className="title">{sugestao.label}</div>
                              <div className="sub">
                                <span className="badge">
                                  {sugestao.quantity} {sugestao.unit}
                                </span>
                                {sugestao.reason ? <span>{sugestao.reason}</span> : null}
                              </div>
                            </div>
                            <div className="actions">
                              {aceita ? (
                                <span className="small muted">na lista</span>
                              ) : (
                                <button
                                  type="button"
                                  className="btn btn-sm"
                                  disabled={salvando}
                                  onClick={() => void aceitar([sugestao])}
                                >
                                  ➕ Adicionar
                                </button>
                              )}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                    <p className="small muted">
                      <Link to="/lista-de-compras">Ver a lista de compras</Link>
                    </p>
                  </>
                )}
              </section>
            </>
          ) : null}
        </>
      )}
    </main>
  );
}
